import { Injectable } from '@angular/core';
import { ActionTypeEnum, TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { Equipment } from '@core/interfaces';
import { LocationEnvironment } from '@core/interfaces/locations-environments';
import { LocationSector } from '@core/interfaces/locations-sectors';
import { TranslateService } from '@ngx-translate/core';
import { flatMapDeep } from 'lodash';
import {
  FormatEnvironmentsParams,
  FormatEquipmentsParams,
  FormatSectorsParams,
  FormatUnlinkedEquipments,
  GetLinkedItemsParams,
  GetLinkedItemsResult,
} from './dialog-link.types';

@Injectable({
  providedIn: 'root'
})
export class DialogLinkService {
  public linkedEquipments: Equipment[] = [];
  public linkedSectors: LocationSector[] = [];
  public linkedEnvironments: LocationEnvironment[] = [];

  public constructor(private readonly translateService: TranslateService) {}

  public getLinkedItems(params: GetLinkedItemsParams): GetLinkedItemsResult {
    const { locationLinks, unlinkedEquipments } = params;
    const sectors = this.mapCheckedItems(locationLinks, 'sector');
    const flattedMaintenanceLinks = flatMapDeep(locationLinks, (item) => item.children);
    const environments = this.mapCheckedItems(flattedMaintenanceLinks, 'environment');
    const equipments = this.mapCheckedItems(flattedMaintenanceLinks, 'equipment');
    const flattedEquipmentsFromEnvironmentsToSave = flatMapDeep(environments, (environment) => environment.children);
    const equipmentsInsideEnvironments = this.mapCheckedItems(flattedEquipmentsFromEnvironmentsToSave, 'equipment');
    equipmentsInsideEnvironments.forEach((equipment) => {
      equipments.push(equipment);
    });

    if (unlinkedEquipments.length) {
      const unlinkedEquipmentsToSave = this.mapCheckedItems(unlinkedEquipments[0].children, 'equipment');
      unlinkedEquipmentsToSave.forEach((equipment) => {
        equipments.push(equipment);
      });
    }

    const sectorsToSave = sectors.map((sector) => sector.original).filter((sector) => !sector.archived);
    this.linkedSectors.forEach((linkedSector) => {
      const existingSector = sectorsToSave.find((item) => item.id === linkedSector.id);
      if (!existingSector) {
        sectorsToSave.push(linkedSector);
      }
    });

    const environmentsToSave = environments.map((environment) => environment.original);
    this.linkedEnvironments.forEach((linkedEnvironment) => {
      const existingEnvironment = environmentsToSave.find((item) => item.id === linkedEnvironment.id);
      if (!existingEnvironment) {
        environmentsToSave.push(linkedEnvironment);
      }
    });

    const equipmentsToSave = equipments.map((equipment) => equipment.original);
    this.linkedEquipments.forEach((linkedEquipment) => {
      const existingEquipment = equipmentsToSave.find((item) => item.id === linkedEquipment.id);
      if (!existingEquipment) {
        equipmentsToSave.push(linkedEquipment);
      }
    });

    environmentsToSave.forEach((env, index) => {
      const hasSector = sectorsToSave.some((sector) => sector.id === env.locationSector.id);
      if (!hasSector) {
        environmentsToSave.splice(index, 1);
      }

      if (env.archived) {
        environmentsToSave.splice(index, 1);
      }
    });

    equipmentsToSave.forEach((eq, index) => {
      if (eq.locationSector) {
        const hasSector = sectorsToSave.some((sector) => sector.id === eq.locationSector.id);
        if (!hasSector) {
          equipmentsToSave.splice(index, 1);
        }
      }

      if (eq.locationEnvironment) {
        const hasEnvironment = environmentsToSave.some((environment) => environment.id === eq.locationEnvironment.id);
        if (!hasEnvironment) {
          equipmentsToSave.splice(index, 1);
        }
      }

      if (eq.archived) {
        equipmentsToSave.splice(index, 1);
      }
    });

    return {
      sectors: sectorsToSave,
      equipments: equipmentsToSave,
      environments: environmentsToSave,
    };
  }

  public formatSectors(params: FormatSectorsParams): TreeViewItem[] {
    const { sectors } = params;
    return (sectors || []).map((sector) => {
      const sectorItem: TreeViewItem = {
        id: sector.id,
        text: sector.name,
        description: sector.description,
        icon: 'sector',
        metadata: 'sector',
        original: sector,
        action: {
          type: ActionTypeEnum.checkbox,
          checked: Boolean((this.linkedSectors || []).some((locationSector) => locationSector.id === sector.id)),
        }
      };

      sectorItem.children = [
        ...this.formatEnvironments({
          environments: sector.locationEnvironments,
          parent: sectorItem,
        }),
        ...this.formatEquipments({
          equipments: sector.equipments,
          parent: sectorItem,
        }),
      ];

      return sectorItem;
    });
  }

  public formatUnlinkedEquipments(params: FormatUnlinkedEquipments): TreeViewItem[] {
    const { unlinkedEquipments } = params;
    const formattedEquipments = this.formatEquipments({ equipments: unlinkedEquipments });

    if (!formattedEquipments.length) {
      return [];
    }

    return [{
      text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.EQUIPMENTS_WITHOUT_SECTOR_ENVIRONMENT'),
      icon: 'more_vertical',
      children: formattedEquipments,
      action: {
        type: ActionTypeEnum.none
      }
    }];
  }

  private formatEnvironments(params: FormatEnvironmentsParams): TreeViewItem[] {
    const { parent, environments } = params;
    return (environments || []).map((environment) => {
      const environmentItem: TreeViewItem = {
        id: environment.id,
        text: environment.name,
        description: environment.description,
        icon: 'environment',
        original: environment,
        parent,
        metadata: 'environment',
        action: {
          type: ActionTypeEnum.checkbox,
          checked: Boolean((this.linkedEnvironments || []).some((locationEnvironment) => locationEnvironment.id === environment.id))
        }
      };

      environmentItem.children = this.formatEquipments({
        parent: environmentItem,
        equipments: environment.equipments,
      });

      return environmentItem;
    });
  }

  private formatEquipments(params: FormatEquipmentsParams): TreeViewItem[] {
    const { equipments, parent } = params;
    return (equipments || []).map((equipment) => ({
      id: equipment.id,
      text: equipment.name,
      description: equipment.number,
      icon: 'equipment',
      image: equipment.imageUrl,
      children: [],
      parent,
      original: equipment,
      metadata: 'equipment',
      action: {
        type: ActionTypeEnum.checkbox,
        checked: Boolean((this.linkedEquipments || []).some((linkedEquipment) => linkedEquipment.id === equipment.id))
      }
    }));
  }

  private mapCheckedItems(treeViewItems: TreeViewItem[], metadata?: string): TreeViewItem[] {
    return treeViewItems.filter((item) => item.metadata === metadata && item.action.checked);
  }
}
